import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { callApi } from "@/dataprovider/misc-apis";

export type StudentOption = {
  id: string;
  name: string;
  admissionNo: number;
  itsId: string;
  status: string;
};

export const studentLabel = (s: StudentOption) =>
  `${s.name} · adm. ${s.admissionNo} · ITS ${s.itsId}`;

/** Server-side student search by name, ITS, admission number, guardian or phone (2+ characters). */
export function StudentPicker({
  value,
  onChange,
  autoFocus,
  label = "Student",
}: {
  value: StudentOption | null;
  onChange: (student: StudentOption | null) => void;
  autoFocus?: boolean;
  label?: string;
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<StudentOption[]>([]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setOptions([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      callApi({ location: "madrasaStudents", method: "GET", data: { q: query.trim(), limit: 15 } })
        .then((r) =>
          setOptions(((r.data as { rows?: StudentOption[] }).rows ?? []) as StudentOption[])
        )
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  return (
    <Autocomplete
      sx={{ flex: 1 }}
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      onInputChange={(_, next) => setQuery(next)}
      getOptionLabel={studentLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      noOptionsText={
        query.trim().length < 2 ? "Type a name, ITS or admission number" : "No students found"
      }
      renderInput={(params) => <TextField {...params} label={label} autoFocus={autoFocus} />}
    />
  );
}
